/**
 * CompanionLayer — the one overlay the whole app shares.
 *
 * Renders the quiet senior colleague as an expandable panel. Its collapsed
 * state lives in each shell's command bar, so it never covers workspace
 * controls. The panel is mounted here once and follows in-shell navigation.
 *
 * Three volumes (COMPANION.md §2):
 *   1. Ambient   — a green command-bar dot; details stay quiet until opened.
 *   2. Feed      — pull the panel open: "Waiting on you" + "FYI".
 *   3. Interrupt — an amber command-bar signal when needs_input is urgent+sure.
 *
 * Mounted once per shell beside the page (see routes.ts). Renders nothing when
 * the Companion is disabled, so the classic Watch + NotificationBell take over.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  AlertTriangle,
  AtSign,
  Bell,
  ChevronDown,
  Clock,
  GitPullRequest,
  Lightbulb,
  Loader2,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  X,
} from "lucide-react";

import { useCompanion } from "./CompanionProvider";
import {
  companionItemDestination,
  type CompanionItem,
  type CompanionTier,
} from "../../../lib/companion";
import {
  getScenarioDecisionObservation,
  type ScenarioDecisionObservation,
} from "../../../lib/decisions";

// Dot / accent colour by tier — calm green, info cyan, attention amber.
const TIER_DOT: Record<CompanionTier, string> = {
  ambient: "bg-emerald-500",
  feed: "bg-cyan-600",
  interrupt: "bg-amber-500",
};

const BRIEFING_LABEL = {
  balanced: "Balanced",
  executive: "Executive",
  operator: "Operator",
  steward: "Steward",
} as const;

function sourceIcon(item: CompanionItem) {
  const cls = "h-4 w-4 shrink-0";
  switch (item.source) {
    case "watch":
      return item.polarity === "bad" ? (
        <AlertTriangle className={`${cls} text-amber-600`} strokeWidth={1.7} />
      ) : (
        <ShieldCheck className={`${cls} text-emerald-600`} strokeWidth={1.7} />
      );
    case "decision":
      return <Clock className={`${cls} text-cyan-700`} strokeWidth={1.7} />;
    case "proactive":
      return item.polarity === "bad" ? (
        <AlertTriangle className={`${cls} text-rose-500`} strokeWidth={1.7} />
      ) : (
        <Lightbulb className={`${cls} text-cyan-700`} strokeWidth={1.7} />
      );
    case "notification":
      if (item.kind === "mention" || item.kind === "comment_reply")
        return <AtSign className={`${cls} text-cyan-700`} strokeWidth={1.7} />;
      if (item.kind.startsWith("mr_"))
        return <GitPullRequest className={`${cls} text-cyan-700`} strokeWidth={1.7} />;
      return <Bell className={`${cls} text-slate-500`} strokeWidth={1.7} />;
    default:
      return <TrendingUp className={`${cls} text-slate-500`} strokeWidth={1.7} />;
  }
}

function actionLabel(item: CompanionItem, primary = false) {
  if (item.kind === "dimension_conformance_review") return "Review and choose";
  return primary ? "Review" : "View details";
}

function displayCopy(item: CompanionItem): { title: string; detail: string } {
  if (item.kind === "dimension_conformance_review") {
    const legacy = item.detail.match(
      /^(.+)\.([^.]+) cannot be safely conformed automatically and needs a decision\.?$/i,
    );
    if (legacy) {
      return {
        title: `Review the match for "${legacy[2]}"`,
        detail: `Meridian found a possible match for this column in ${legacy[1]}. Open the guided review to compare the values and choose what happens.`,
      };
    }
  }

  if (item.kind === "hierarchy_change") {
    const legacyTitle = item.title.match(/^(.*?) v\d+ affects (\d+) models?$/i);
    const legacyDetail = item.detail.match(/^Published (.+?)\. Review /i);
    if (legacyTitle && legacyDetail) {
      const changes = legacyDetail[1]
        .replace(/\bcreated\b/gi, "added")
        .replace(/\bupdated\b/gi, "changed")
        .replace(/\bdeleted\b/gi, "removed")
        .replace(/, /g, " · ");
      const modelCount = Number(legacyTitle[2]);
      return {
        title: `${legacyTitle[1]} was updated`,
        detail: `${changes.charAt(0).toUpperCase()}${changes.slice(1)}. Used by ${modelCount} model${modelCount === 1 ? "" : "s"}.`,
      };
    }
  }

  return { title: item.title, detail: item.detail };
}

function digestText(needsYou: CompanionItem[], feed: CompanionItem[], ambient: CompanionItem[]) {
  if (needsYou.length > 0) {
    const count = needsYou.length;
    const title = displayCopy(needsYou[0]).title.replace(/[.!?]+$/, "");
    return `${count} decision${count === 1 ? "" : "s"} need${count === 1 ? "s" : ""} you. ${title}.`;
  }
  const updates = [...feed, ...ambient];
  if (updates.length === 0) return "All quiet.";
  const title = displayCopy(updates[0]).title.replace(/[.!?]+$/, "");
  return `${updates.length} recent update${updates.length === 1 ? "" : "s"}. ${title}.`;
}

export function CompanionLayer() {
  const ctx = useCompanion();
  const navigate = useNavigate();

  if (!ctx || !ctx.enabled || !ctx.data) return null;
  const { data, open, setOpen, act } = ctx;
  const { presence, interrupt, needs_you, feed } = data;
  const ambient = data.ambient ?? [];
  const interruptCopy = interrupt ? displayCopy(interrupt) : null;

  function follow(item: CompanionItem) {
    void act(item.key, "open");
    const link = companionItemDestination(item);
    if (!link) return;
    setOpen(false);
    if (link.startsWith("/")) navigate(link);
    else window.open(link, "_blank", "noopener");
  }

  // The shell command bar is the single collapsed launcher. Rendering nothing
  // here keeps Companion state from covering page-level actions.
  if (!open) {
    return null;
  }

  // ---- Expanded: the panel --------------------------------------------------
  return (
    <div className="fixed right-5 top-16 z-40 flex max-h-[calc(100vh-5rem)] w-[340px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-start gap-2 border-b border-slate-100 px-4 py-3">
        <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${TIER_DOT[presence.tier]}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-cyan-700" strokeWidth={2} />
            <span className="text-[13px] font-semibold text-slate-900">Meridian</span>
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-medium text-slate-500">
              {BRIEFING_LABEL[data.briefing_lens]} briefing
            </span>
          </div>
          <p className="mt-0.5 text-[12px] leading-snug text-slate-500">
            {digestText(needs_you, feed, ambient)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Collapse"
          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      <div className="max-h-[60vh] overflow-y-auto">
        {/* Interrupt banner */}
        {interrupt && (
          <div className="border-b border-amber-100 bg-amber-50 px-4 py-3">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" strokeWidth={1.9} />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                Needs your call
              </span>
            </div>
            <p className="mt-1 text-[13px] font-medium text-amber-950">{interruptCopy?.title}</p>
            {interruptCopy?.detail && (
              <p className="mt-0.5 text-[12px] leading-snug text-amber-800">
                {interruptCopy.detail}
              </p>
            )}
            {interrupt.priority_reason && (
              <p className="mt-1 text-[10.5px] font-medium text-amber-700">
                {interrupt.priority_reason}
              </p>
            )}
            <div className="mt-2 flex items-center gap-2">
              {companionItemDestination(interrupt) && (
                <button
                  type="button"
                  onClick={() => follow(interrupt)}
                  className="rounded-md bg-amber-600 px-2.5 py-1 text-[12px] font-medium text-white transition-colors hover:bg-amber-700"
                >
                  {actionLabel(interrupt, true)}
                </button>
              )}
              <button
                type="button"
                onClick={() => act(interrupt.key, "snooze", 60)}
                className="rounded-md border border-amber-300 px-2.5 py-1 text-[12px] font-medium text-amber-900 transition-colors hover:bg-amber-100"
              >
                Later
              </button>
            </div>
          </div>
        )}

        {/* Waiting on you */}
        {needs_you.length > 0 && (
          <Section label="Needs your input">
            {needs_you.map((item) => (
              <Row key={item.key} item={item} onFollow={follow} onAct={act} primary />
            ))}
          </Section>
        )}

        {/* FYI */}
        {feed.length > 0 && (
          <Section label="When you have a sec">
            {feed.map((item) => (
              <Row key={item.key} item={item} onFollow={follow} onAct={act} />
            ))}
          </Section>
        )}

        {/* Ambient updates stay quiet until the user deliberately opens this panel. */}
        {ambient.length > 0 && (
          <Section label="Recent changes">
            {ambient.map((item) => (
              <Row key={item.key} item={item} onFollow={follow} onAct={act} />
            ))}
          </Section>
        )}

        {/* A true empty state: no hidden items behind an "All quiet" message. */}
        {needs_you.length === 0 &&
          feed.length === 0 &&
          ambient.length === 0 &&
          !interrupt && (
          <div className="px-4 py-8 text-center">
            <p className="text-[13px] text-slate-500">All quiet.</p>
            <p className="mt-1 text-[12px] text-slate-400">
              I'll let you know if anything comes up.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <p className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
        {label}
      </p>
      <div className="pb-1">{children}</div>
    </div>
  );
}

function Row({
  item,
  onFollow,
  onAct,
  primary = false,
}: {
  item: CompanionItem;
  onFollow: (item: CompanionItem) => void;
  onAct: (key: string, action: "dismiss" | "done" | "snooze", minutes?: number) => void;
  primary?: boolean;
}) {
  const copy = displayCopy(item);
  return (
    <div className="group flex items-start gap-2.5 px-4 py-2 transition-colors hover:bg-slate-50">
      <span className="mt-0.5">{sourceIcon(item)}</span>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() =>
            companionItemDestination(item) ? onFollow(item) : undefined
          }
          className={`block text-left text-[13px] leading-snug text-slate-800 ${
            companionItemDestination(item) ? "hover:text-cyan-700" : "cursor-default"
          }`}
        >
          {copy.title}
        </button>
        {copy.detail && (
          <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug text-slate-500">
            {copy.detail}
          </p>
        )}
        {item.uncertain && (
          <p className="mt-0.5 text-[11px] italic text-slate-400">
            I'm not fully sure on this one — worth a check.
          </p>
        )}
        {item.priority_reason && (
          <p className="mt-1 text-[10.5px] font-medium text-cyan-700">
            {item.priority_reason}
          </p>
        )}
        <ScenarioObservationPreview item={item} />
        {companionItemDestination(item) && (
          <button
            type="button"
            onClick={() => onFollow(item)}
            className={`mt-2 rounded-md px-2.5 py-1 text-[11.5px] font-medium transition-colors ${
              primary
                ? "bg-cyan-700 text-white hover:bg-cyan-800"
                : "border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
            }`}
          >
            {actionLabel(item, primary)}
          </button>
        )}
        {/* Secondary actions stay quiet until the row is hovered or focused. */}
        <div className="mt-1.5 flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {primary && item.kind !== "dimension_conformance_review" && (
            <button
              type="button"
              onClick={() => onAct(item.key, "done")}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium text-emerald-700 hover:text-emerald-800"
            >
              Done
            </button>
          )}
          {primary && (
            <button
              type="button"
              onClick={() => onAct(item.key, "snooze", 60)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium text-slate-500 hover:text-slate-700"
            >
              <Clock className="h-3 w-3" /> Snooze
            </button>
          )}
          <button
            type="button"
            onClick={() => onAct(item.key, "dismiss")}
            className="inline-flex items-center gap-1 text-[11.5px] font-medium text-slate-400 hover:text-slate-600"
          >
            <X className="h-3 w-3" /> Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

const OBSERVATION_NUMBER = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});
const OBSERVATION_PERCENT = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 1,
});
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function scenarioDecisionTarget(
  item: CompanionItem,
): { modelId: string; decisionId: string } | null {
  if (
    item.source !== "decision" ||
    item.kind !== "scenario_review_due" ||
    !item.link ||
    !item.context_object ||
    !UUID_PATTERN.test(item.context_object)
  ) {
    return null;
  }
  const match = item.link.match(/^\/models\/([^/?#]+)\/insights(?:[?#]|$)/);
  const modelId = match?.[1] ?? "";
  if (!UUID_PATTERN.test(modelId)) return null;
  return { modelId, decisionId: item.context_object };
}

function observationMovement(
  absolute: number | null,
  relative: number | null,
): string {
  const absoluteText =
    absolute == null
      ? null
      : `${absolute > 0 ? "+" : ""}${OBSERVATION_NUMBER.format(absolute)}`;
  const relativeText =
    relative == null
      ? null
      : `${relative > 0 ? "+" : ""}${OBSERVATION_PERCENT.format(relative)}`;
  return [absoluteText, relativeText && `(${relativeText})`]
    .filter(Boolean)
    .join(" ");
}

function unavailableObservationText(
  observation: ScenarioDecisionObservation,
): string {
  if (observation.reason === "scope_fingerprint_missing") {
    return "This older promotion cannot be compared under a verified data scope.";
  }
  if (observation.reason === "entitlement_scope_changed") {
    return "Your governed data scope changed, so Meridian withheld the comparison.";
  }
  if (observation.reason === "decision_owner_inactive") {
    return "The original Decision owner no longer has an active data scope.";
  }
  return "A current governed comparison is not available.";
}

function ScenarioObservationPreview({ item }: { item: CompanionItem }) {
  const target = scenarioDecisionTarget(item);
  const [observation, setObservation] =
    useState<ScenarioDecisionObservation | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!target) {
      setObservation(null);
      setLoading(false);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setObservation(null);
    setLoading(true);
    setFailed(false);
    void getScenarioDecisionObservation(target.modelId, target.decisionId)
      .then((result) => {
        if (!cancelled) setObservation(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target?.decisionId, target?.modelId]);

  if (!target) return null;
  if (loading) {
    return (
      <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-cyan-700">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking current governed measures…
      </p>
    );
  }
  if (failed) {
    return (
      <p className="mt-1.5 text-[11px] text-slate-400">
        Current comparison could not be loaded.
      </p>
    );
  }
  if (!observation) return null;
  if (observation.status === "unavailable") {
    return (
      <p className="mt-1.5 text-[11px] leading-snug text-amber-700">
        {unavailableObservationText(observation)}
      </p>
    );
  }

  const measures = observation.measures
    .filter((measure) => measure.status === "observed")
    .slice(0, 2);
  if (measures.length === 0) return null;
  return (
    <div className="mt-1.5 rounded-md border border-cyan-100 bg-cyan-50/60 px-2 py-1.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-cyan-800">
        Current observation
      </p>
      {measures.map((measure) => (
        <p
          key={`${measure.table}:${measure.column}`}
          className="mt-0.5 text-[11px] leading-snug text-slate-700"
        >
          <strong>{measure.column}</strong>{" "}
          {measure.observed_value == null
            ? "—"
            : OBSERVATION_NUMBER.format(measure.observed_value)}
          {measure.absolute_change != null || measure.relative_change != null
            ? ` · ${observationMovement(
                measure.absolute_change,
                measure.relative_change,
              )} vs promoted`
            : ""}
        </p>
      ))}
      <p className="mt-1 text-[10px] leading-snug text-slate-500">
        Governed live data; movement is not causal proof.
      </p>
    </div>
  );
}
